/* =====================================================================
   SIZE DROPDOWN A/B TEST — product pages
   Design doc: docs/superpowers/specs/2026-08-27-size-dropdown-ab-test-design.md
   Loader:     layout/theme.liquid (deferred, after pdp-redesign.js)

   Experiment flag: 'dropdown-variants'   |   test arm: 'dropdown'

   Replaces the grid of square size chips with a single dropdown, the way
   schuh does it. Only the SIZE option changes; width chips and colour
   swatches are untouched in both arms.

   How it works
   ------------
   A veneer, not a second form. The theme's size radios stay in the DOM and
   stay the source of truth for `theme.ProductForm`; the chips are only
   hidden. Choosing an option checks the matching radio and fires the same
   bubbling `change` a chip click fires, so price, availability, add to
   basket, the sticky bar and the conversion hints all run unmodified.

   Option labels are read from the spans the size grid already filled
   ('.size-btn__uk' / '.size-btn__eu') rather than re-derived, so junior
   sizing, EU-only brands, half sizes and a shopper's stored EU-first
   preference match the control arm by construction.

   Unlike schuh, the dropdown opens on the size the page already has
   selected. Shopify pre-selects `selected_or_first_available_variant`, and
   `theme.ProductForm` reads `.find(radio => radio.checked).value` unguarded,
   so leaving nothing checked would break the form. The placeholder option is
   a disabled fallback for that case, not a state shoppers can return to.

   Every failure path lands on control: no PostHog, flag unresolved or any
   other value, no size option on the product, no chips, thrown error.

   Exposure ($feature_flag_called) is only fired once a size selector has
   been confirmed on the page, so both arms enrol on identical conditions.

   QA
   --
   ?ab_debug (or localhost) logs as [size-dropdown-ab].
   Force the arm:
     posthog.featureFlags.overrideFeatureFlags({flags:{'dropdown-variants':'dropdown'}})
   `window.__applySizeDropdown()` builds it by hand, ignoring the flag.

   Teardown
   --------
   Lost: delete this file and its <script> tag in layout/theme.liquid.
   Nothing else changes. Won: drop the flag read in applyExperiment() so
   build() runs unconditionally, then fold into assets/pdp-redesign.js.
   ===================================================================== */
(function () {
  'use strict';

  var FLAG = 'dropdown-variants';
  var ARM = 'dropdown';
  var ON_CLASS = 'swatches__container--nsl-dropdown';
  var SOLD_OUT_SUFFIX = ' - Sold out';
  var PLACEHOLDER = 'Please select a size.';

  var DEBUG = (window.location.search.indexOf('ab_debug') !== -1) ||
    (window.location.hostname === 'localhost') ||
    (window.location.hostname.indexOf('127.0.0.1') === 0);

  var built = false;
  var started = false;
  var chips = [];
  var select = null;

  function log() {
    if (!DEBUG || !window.console) return;
    try {
      console.log.apply(console, ['[size-dropdown-ab]'].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  function container() {
    return document.querySelector('.product__section .swatches__container[data-size-option]') ||
      document.querySelector('.swatches__container[data-size-option]');
  }

  /* "UK 8 (EU 42)" from the spans the size grid filled, falling back to the
     raw option value when the size tables could not parse the chip. */
  function labelFor(chip) {
    var primaryEl = chip.querySelector('.size-btn__uk');
    var secondaryEl = chip.querySelector('.size-btn__eu');
    if (primaryEl) {
      var primary = (primaryEl.textContent || '').trim();
      var secondary = secondaryEl ? (secondaryEl.textContent || '').trim() : '';
      if (primary) return secondary ? primary + ' (' + secondary + ')' : primary;
    }

    var label = chip.querySelector('.swatches__form--label');
    var raw = label ? (label.getAttribute('data-size-value') || '').trim() : '';
    if (!raw && label) {
      /* The label carries a visually hidden "sold out or unavailable" span. */
      var clone = label.cloneNode(true);
      var hidden = clone.querySelector('.label-unavailable');
      if (hidden && hidden.parentNode) hidden.parentNode.removeChild(hidden);
      raw = (clone.textContent || '').trim();
    }
    if (raw) return raw;

    var input = chip.querySelector('input[type="radio"]');
    return input ? input.value : '';
  }

  function radioOf(chip) {
    return chip.querySelector('input[type="radio"]');
  }

  /* Option labels, sold out states and the select's own value, brought back
     into line with the radios. Idempotent, and safe to call on every change:
     the theme re-computes which sizes are in stock whenever width or colour
     moves, and re-checks a size radio directly without firing an event. */
  function refresh() {
    if (!select) return;
    var selectedIndex = -1;

    chips.forEach(function (chip, i) {
      var option = select.options[i + 1];   /* +1 for the placeholder */
      if (!option) return;

      var soldOut = chip.classList.contains('soldout');
      var text = labelFor(chip);
      var wanted = soldOut ? text + SOLD_OUT_SUFFIX : text;
      if (option.textContent !== wanted) option.textContent = wanted;
      option.disabled = soldOut;

      var radio = radioOf(chip);
      if (radio && radio.checked) selectedIndex = i;
    });

    /* A disabled option can still be shown as selected, which is what should
       happen when a width change leaves the checked size out of stock. */
    select.value = selectedIndex === -1 ? '' : String(selectedIndex);
  }

  function build() {
    if (built) return false;

    var c = container();
    if (!c) { log('no size option on this product'); return false; }

    chips = [].slice.call(c.querySelectorAll('.js-swatch-element'));
    if (!chips.length) { log('size container has no chips'); return false; }

    injectStyles();
    c.classList.add(ON_CLASS);
    /* Undo the redesign's five-column grid. Removing the class beats trying to
       override `html.pdp-redesign .product__section .swatches__container--size-grid
       { display: grid !important }`, and it takes the chips' grid-column rules
       with it. NSL.buildSizeGrid() re-adds it if it ever runs again, which is
       why build() also runs on shopify:section:load. */
    c.classList.remove('swatches__container--size-grid');

    select = document.createElement('select');
    select.className = 'styled-select nsl-size-dropdown';
    select.id = 'nsl-size-dropdown';

    var heading = c.querySelector('.swatches__option-name > strong');
    var name = heading ? (heading.textContent || '').replace(/:\s*$/, '').trim() : 'Size';
    select.setAttribute('aria-label', name);

    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = PLACEHOLDER;
    placeholder.disabled = true;
    placeholder.hidden = true;
    select.appendChild(placeholder);

    chips.forEach(function (chip, i) {
      var option = document.createElement('option');
      option.value = String(i);
      select.appendChild(option);
    });

    select.addEventListener('change', function () {
      var chip = chips[Number(select.value)];
      if (!chip) return;
      var radio = radioOf(chip);
      if (!radio || radio.disabled) return;
      if (radio.checked) return;

      radio.checked = true;
      /* Exactly what clicking a chip does: the container is the theme's
         .js-variant-selector, and both it and the radio itself are listening. */
      radio.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: true }));
      log('selected', labelFor(chip));
    });

    c.insertBefore(select, chips[0]);
    refresh();
    watch(c);

    built = true;
    log('built with', chips.length, 'sizes');
    return true;
  }

  function watch(c) {
    /* Only class attributes: the theme flips `soldout` on the swatch elements
       when width or colour changes. Deliberately not childList — refresh()
       rewrites option text inside this container and would retrigger itself. */
    if ('MutationObserver' in window) {
      var timer;
      new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(refresh, 30);
      }).observe(c, { attributes: true, attributeFilter: ['class'], subtree: true });
    }

    /* Radios are re-checked as a property, which mutates nothing observable,
       so also refresh after the theme's own change handlers have run. */
    document.addEventListener('change', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target === select) return;
      if (!e.target.closest('.js-variant-selector')) return;
      setTimeout(refresh, 60);
    });
  }

  function injectStyles() {
    if (document.getElementById('nsl-size-dropdown-styles')) return;
    var css = [
      /* The redesign lays the container out as a grid from a selector that
         outranks anything at this specificity, so build() drops that class
         outright rather than fight it. This is the belt to that braces, and
         it is also what applies if the redesign is ever switched off. */
      'html .product__section .' + ON_CLASS + '.swatches__container{display:block !important;}',
      '.product__section .' + ON_CLASS + ' .swatches__swatch--size,',
      '.product__section .' + ON_CLASS + ' .swatches__swatch--regular,',
      '.product__section .' + ON_CLASS + ' .size-grid__helper,',
      '.product__section .' + ON_CLASS + ' .pdp-r-unit,',
      '.product__section .' + ON_CLASS + ' .pdp-r-size-legend{display:none !important;}',
      '.product__section .' + ON_CLASS + ' .swatches__option-name{margin-bottom:6px !important;}',
      'select.nsl-size-dropdown{',
      'width:100%;max-width:100%;height:52px;line-height:normal;margin:0 0 4px;',
      'padding:0 40px 0 14px;',
      'border:1px solid var(--pr-chip, #cccccc);border-radius:var(--pr-radius, 8px);',
      'background:#ffffff var(--select-arrow-bg) no-repeat right 14px center;',
      'font-family:var(--pr-body, inherit);font-size:15px;color:var(--pr-text, #333333);',
      'cursor:pointer;-webkit-appearance:none;-moz-appearance:none;appearance:none;',
      '}',
      'select.nsl-size-dropdown:focus{outline:2px solid var(--pr-teal, #00b9bb);outline-offset:1px;}',
      'select.nsl-size-dropdown option:disabled{color:#999999;}'
    ].join('');

    var style = document.createElement('style');
    style.id = 'nsl-size-dropdown-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function applyExperiment() {
    /* Reading the flag IS the exposure event, so it must not happen until a
       size selector is known to be on the page. Without that check, shoppers
       on products with no size option would be enrolled in an experiment they
       could never have seen. */
    if (!container()) { log('no size selector; not enrolling'); return; }

    if (!window.posthog || typeof window.posthog.getFeatureFlag !== 'function') {
      log('posthog unavailable; staying on control');
      return;
    }

    var value;
    try { value = window.posthog.getFeatureFlag(FLAG); }
    catch (e) { log('error reading flag', e); return; }

    log('variant =', value);
    if (value !== ARM) return;

    try { build(); }
    catch (e) { log('build failed; staying on control', e); }
  }

  function whenFlagsReady(cb) {
    /* posthog.init runs on DOMContentLoaded in theme.liquid, so the stub may
       not be ready yet. onFeatureFlags fires on load and again on any
       override, which is what makes the QA hook above work. */
    if (window.posthog && typeof window.posthog.onFeatureFlags === 'function') {
      window.posthog.onFeatureFlags(cb);
      return;
    }
    var tries = 0;
    var timer = setInterval(function () {
      if (window.posthog && typeof window.posthog.onFeatureFlags === 'function') {
        clearInterval(timer);
        window.posthog.onFeatureFlags(cb);
      } else if (++tries > 40) {   /* ~6s */
        clearInterval(timer);
        log('posthog never became ready; staying on control');
      }
    }, 150);
  }

  function start() {
    if (started) return;
    started = true;
    whenFlagsReady(applyExperiment);
  }

  /* Manual hook for QA: builds the dropdown without touching the flag. */
  window.__applySizeDropdown = function () { return build(); };

  /* Deferred, so this runs before DOMContentLoaded, and both theme.js
     (theme.Product) and pdp-redesign.js (the size grid this reads its labels
     from) initialise on that event. Wait for it. */
  if (document.readyState === 'complete') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
    window.addEventListener('load', start);
  }

  document.addEventListener('shopify:section:load', function () {
    built = false;
    select = null;
    setTimeout(applyExperiment, 200);
  });
})();